import { parseISO, format } from 'date-fns';
import type { GenreStats } from '@/components/analytics/GenreDashboard';
import type { MoodDataPoint } from '@/components/analytics/MoodArcChart';

// Genre mood mapping (imported from wrappedUtils pattern)
const GENRE_MOOD_MAP: Record<string, number> = {
  'Horror': -1.0,
  'Thriller': -0.8,
  'Mystery': -0.6,
  'True Crime': -0.9,
  'War': -0.8,
  'Tragedy': -0.7,
  'Dark': -0.8,
  'Crime': -0.7,
  'Suspense': -0.6,
  'Gothic': -0.7,
  'Fiction': 0,
  'Non-fiction': 0,
  'Biography': 0,
  'History': -0.2,
  'Science': 0.1,
  'Philosophy': -0.1,
  'Business': 0,
  'Politics': -0.2,
  'Romance': 0.8,
  'Comedy': 1.0,
  'Humor': 1.0,
  'Self-help': 0.6,
  'Inspiration': 0.7,
  'Travel': 0.5,
  'Cooking': 0.4,
  'Art': 0.3,
  'Poetry': 0.2,
  'Fantasy': 0.4,
  'Adventure': 0.5,
  'Contemporary': 0.3,
  'Young Adult': 0.4
};

interface BookWithGenreData {
  finished_at?: string;
  started_at?: string;
  rating_star?: number;
  books?: {
    page_count?: number;
    subjects?: string[];
  };
}

function calculateBookMood(subjects?: string[]): number {
  if (!subjects || subjects.length === 0) return 0;
  
  const scores = subjects
    .map(subject => {
      if (GENRE_MOOD_MAP[subject]) return GENRE_MOOD_MAP[subject];
      
      for (const [genre, score] of Object.entries(GENRE_MOOD_MAP)) {
        if (subject.toLowerCase().includes(genre.toLowerCase())) {
          return score;
        }
      }
      
      return 0;
    })
    .filter(score => score !== 0);
  
  if (scores.length === 0) return 0;
  
  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
}

function getReadingDuration(book: BookWithGenreData): number | null {
  if (!book.started_at || !book.finished_at) return null;

  const days = Math.ceil(
    (new Date(book.finished_at).getTime() - new Date(book.started_at).getTime()) / (1000 * 60 * 60 * 24)
  );
  return days > 0 ? days : null;
}

export function calculateGenreStats(books: BookWithGenreData[]): GenreStats[] {
  const genreMap = new Map<string, {
    count: number;
    totalRating: number;
    ratedCount: number;
    totalPages: number;
    totalDays: number;
    daysCount: number;
  }>();

  books.forEach(book => {
    if (!book.books?.subjects) return;

    const duration = getReadingDuration(book);

    book.books.subjects.forEach(genre => {
      const current = genreMap.get(genre) || {
        count: 0,
        totalRating: 0,
        ratedCount: 0,
        totalPages: 0,
        totalDays: 0,
        daysCount: 0,
      };

      current.count++;
      current.totalPages += book.books!.page_count || 0;

      if (book.rating_star) {
        current.totalRating += book.rating_star;
        current.ratedCount++;
      }

      if (duration) {
        current.totalDays += duration;
        current.daysCount++;
      }

      genreMap.set(genre, current);
    });
  });

  return Array.from(genreMap.entries())
    .map(([genre, data]) => ({
      genre,
      count: data.count,
      averageRating: data.ratedCount > 0 ? data.totalRating / data.ratedCount : 0,
      totalPages: data.totalPages,
      averageDays: data.daysCount > 0 ? Math.round(data.totalDays / data.daysCount) : 0,
    }))
    .sort((a, b) => b.count - a.count);
}

export function calculateMoodJourney(books: BookWithGenreData[]): MoodDataPoint[] {
  const monthData: Record<string, { totalMood: number; bookCount: number }> = {};

  books.forEach(book => {
    if (!book.finished_at || !book.books?.subjects) return;

    const month = format(parseISO(book.finished_at), 'MMM');
    const mood = calculateBookMood(book.books.subjects);

    if (!monthData[month]) {
      monthData[month] = { totalMood: 0, bookCount: 0 };
    }

    monthData[month].totalMood += mood;
    monthData[month].bookCount++;
  });

  const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return monthOrder
    .filter(month => monthData[month])
    .map(month => ({
      month,
      moodScore: monthData[month].totalMood / monthData[month].bookCount,
      bookCount: monthData[month].bookCount,
    }));
}
